import Immutable from 'immutable';

import ErrorData from 'sharedreact/data/ErrorData';
import type Filter from 'sharedreact/data/Filter';
import GroupAndCount from 'sharedreact/data/GroupAndCount';
import type SortOrder from 'sharedreact/data/SortOrder';
import UploadFileResult from 'sharedreact/data/UploadFileResult';
import UploadedFile from 'sharedreact/data/UploadedFile';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import ConnectionSupport from 'sharedreact/utils/ConnectionSupport';
import FilterSupport from 'sharedreact/utils/FilterSupport';

import FontFile from 'workspaces/launcher/data/FontFile';

type OnUploadSuccess = (uploadFileResult: UploadFileResult) => void;
type OnCheckConfirmSuccess = (needsConfirmOverwrite: boolean) => void;

type OnFetchSuccess = (
  range: RangeType,
  totalCount: number,
  items: Immutable.List<FontFile>,
  countPerGroup: Immutable.List<GroupAndCount>,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder) => void;

type OnRemoveSuccess =() => void;
type OnError = (error: ErrorData) => void;

function uploadFonts (files: FileList | File[] | Immutable.List<UploadedFile>, forceOverwrite: boolean, onSuccess: OnUploadSuccess, onError: OnError): void {
  if (Immutable.isList(files)) {
    ConnectionSupport.upload(
      'uploadFonts',
      [],
      {
        files: files.toJS(),
        forceOverwrite: forceOverwrite
      },
      (data) => onSuccess(UploadFileResult.fromJS(data)),
      onError
    );
  } else {
    ConnectionSupport.upload(
      'uploadFonts',
      files,
      {
        forceOverwrite: forceOverwrite
      },
      (data) => onSuccess(UploadFileResult.fromJS(data)),
      onError
    );
  }
}

function checkIfNeedsOverwrite (fileNames: string[], onSuccess: OnCheckConfirmSuccess, onError: OnError) {
  ConnectionSupport.post(
    'fontsNeedOverwrite',
    { fileNames: fileNames },
    onSuccess,
    onError);
}

const _filterKeywords = ['name', 'modificationdate'];

function fetch (
  range: RangeType,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder,
  isInitialFetch: boolean,
  onSuccess: OnFetchSuccess,
  onError: OnError
) {
  const expandedFilter: Filter = FilterSupport.expand(
    filter,
    _filterKeywords
  );

  const expandedAdvancedFilter: Filter = FilterSupport.expand(
    advancedFilter,
    _filterKeywords
  );

  ConnectionSupport.post(
    'getFonts',
    {
      queryData: {
        range: range,
        filter: expandedFilter,
        advancedFilter: expandedAdvancedFilter,
        groupBy: groupBy,
        sortOrder: sortOrder
      },
      isInitialFetch: isInitialFetch
    },
    (data) => onSuccess(
      data.range,
        data.totalCount,
        Immutable.List(data.items.map((font: any) => FontFile.fromJS(font))),
        Immutable.List(data.countPerGroup.map(GroupAndCount.fromJS)),
        filter,
        advancedFilter,
        groupBy,
        sortOrder
    ),
    onError
  );
}

function remove (ids: Immutable.Set<string>, onSuccess: OnRemoveSuccess, onError: OnError): void {
  ConnectionSupport.post(
    'removeFonts',
    { ids: ids },
    onSuccess,
    onError
  );
}

export default {
  uploadFonts,
  checkIfNeedsOverwrite,
  fetch,
  remove
};
